/**
 * PartnerPortfolioPage - Cross-tenant portfolio view for partner users
 * Feature flagged: features.scoping.partnerTenant
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, FileCheck, Link2, FileText, AlertTriangle,
  Download, RefreshCw, Crown, ChevronRight, Filter,
  CheckCircle, GitBranch, TrendingUp, TrendingDown, Eye
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useProductMode } from '../context/ProductModeContext';
import { trackEvent, TELEMETRY_EVENTS } from '../utils/telemetry';
import { toast } from 'sonner';

// Export Bundle Modal
const ExportBundleModal = ({ isOpen, onClose, tenant, projects, onExport }) => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [options, setOptions] = useState({
    include_baselines: true,
    include_decisions: true,
    include_documents: true,
    include_signoffs: true,
    include_drift: true,
    white_label: false,
    white_label_company_name: ''
  });
  const [exporting, setExporting] = useState(false);
  
  if (!isOpen) return null;
  
  const handleExport = async () => {
    if (!selectedProject) {
      toast.error('Please select a project');
      return;
    }
    
    setExporting(true);
    try {
      await onExport(tenant.tenant_id, selectedProject, options);
      onClose();
    } catch (err) {
      toast.error('Export failed: ' + (err.message || 'Unknown error'));
    } finally {
      setExporting(false);
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto" data-testid="export-modal">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Export Governance Bundle</h3>
          <p className="text-sm text-gray-500">{tenant?.name}</p>
        </div>
        
        <div className="p-6 space-y-4">
          {/* Project selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Project *
            </label>
            <select
              value={selectedProject || ''}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none"
              data-testid="export-project-select"
            >
              <option value="">Choose a project...</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          
          {/* Include options */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Include in Export
            </label>
            <div className="space-y-2">
              {[
                { key: 'include_baselines', label: 'Baselines', icon: GitBranch },
                { key: 'include_decisions', label: 'Decision Links', icon: Link2 },
                { key: 'include_documents', label: 'Documents Index', icon: FileText },
                { key: 'include_signoffs', label: 'Sign-offs', icon: CheckCircle },
                { key: 'include_drift', label: 'Drift Log', icon: AlertTriangle }
              ].map(opt => (
                <label key={opt.key} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={options[opt.key]}
                    onChange={(e) => setOptions(prev => ({ ...prev, [opt.key]: e.target.checked }))}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <opt.icon className="w-4 h-4 text-gray-400" />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
          
          {/* White label option */}
          <div className="pt-2 border-t border-gray-100">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={options.white_label}
                onChange={(e) => setOptions(prev => ({ ...prev, white_label: e.target.checked }))}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span>White-label export (custom branding)</span>
            </label>
            {options.white_label && (
              <input
                type="text"
                placeholder="Company name for header"
                value={options.white_label_company_name}
                onChange={(e) => setOptions(prev => ({ ...prev, white_label_company_name: e.target.value }))}
                className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
              />
            )}
          </div>
        </div>
        
        <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={!selectedProject || exporting}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            data-testid="export-bundle-btn"
          >
            {exporting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {exporting ? 'Exporting...' : 'Export Bundle'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Page Component
const PartnerPortfolioPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getFeature, productMode } = useProductMode();
  
  const tenantId = user?.tenant?.id || user?.tenant_id;
  
  const [loading, setLoading] = useState(true);
  const [portfolio, setPortfolio] = useState(null);
  const [projects, setProjects] = useState({});  // tenant_id -> projects[]
  const [error, setError] = useState(null);
  const [exportModal, setExportModal] = useState({ open: false, tenant: null });
  
  // Check feature flag using getFeature
  const partnerEnabled = useMemo(() => {
    if (typeof getFeature !== 'function') return false;
    return getFeature('scoping.partnerTenant', false);
  }, [getFeature]);
  
  useEffect(() => {
    if (partnerEnabled) {
      loadPortfolio();
    } else {
      setLoading(false);
    }
  }, [partnerEnabled, tenantId, user?.id]);
  
  const loadPortfolio = async () => {
    if (!tenantId || !user?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const res = await api.getPartnerPortfolio(user.id, tenantId);
      setPortfolio(res.data);
      
      // Track telemetry
      trackEvent(TELEMETRY_EVENTS.PARTNER_PORTFOLIO_VIEW_OPENED, {
        tenant_count: res.data.tenants?.length || 0
      });
      
      // Load projects for each tenant
      const projectPromises = res.data.tenants?.map(async (t) => {
        try {
          const projRes = await api.getScopingProjects(t.tenant_id);
          return { tenantId: t.tenant_id, projects: projRes.data.projects || [] };
        } catch {
          return { tenantId: t.tenant_id, projects: [] };
        }
      }) || [];
      
      const projectResults = await Promise.all(projectPromises);
      const projectMap = {};
      projectResults.forEach(r => {
        projectMap[r.tenantId] = r.projects;
      });
      setProjects(projectMap);
      
    } catch (err) {
      console.error('Failed to load partner portfolio:', err);
      setError(err.response?.data?.detail || 'Failed to load portfolio');
    } finally {
      setLoading(false);
    }
  };
  
  const handleExport = async (tenantId, projectId, options) => {
    try {
      const res = await api.exportGovernanceBundle({
        tenant_id: tenantId,
        project_id: projectId,
        ...options
      }, user.id, user.tenant_id);
      
      // Track telemetry
      trackEvent(TELEMETRY_EVENTS.PARTNER_EXPORT_BUNDLE_GENERATED, {
        target_tenant: tenantId,
        project_id: projectId,
        white_label: options.white_label
      });
      
      // Download as JSON
      const blob = new Blob([JSON.stringify(res.data.bundle, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `governance_export_${res.data.export_id}.json`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${res.data.artifact_counts.baselines} baselines, ${res.data.artifact_counts.decisions_links} decisions, ${res.data.artifact_counts.documents} documents`);
      
    } catch (err) {
      throw err;
    }
  };
  
  // Feature gate
  if (!partnerEnabled) {
    return (
      <div className="p-8" data-testid="partner-feature-gate">
        <div className="max-w-lg mx-auto text-center">
          <Crown className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Partner Tenant Mode
          </h2>
          <p className="text-gray-600 mb-6">
            Partner Tenant Mode is not enabled for your organization. 
            Contact your administrator to enable cross-tenant portfolio management.
          </p>
          <button
            onClick={() => navigate('/ai-scoping-assistant')}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Back to Support AI
          </button>
        </div>
      </div>
    );
  }
  
  // Loading
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
      </div>
    );
  }
  
  // Error
  if (error) {
    return (
      <div className="p-8">
        <div className="max-w-lg mx-auto text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={loadPortfolio}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="partner-portfolio-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Partner Portfolio</h1>
          <p className="text-gray-500 mt-1">
            Cross-tenant governance overview • {portfolio?.total_projects || 0} projects across {portfolio?.tenants?.length || 0} clients
          </p>
        </div>
        <button
          onClick={loadPortfolio}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        {[
          { label: 'Approved Baselines', value: portfolio?.summary?.approved_baselines || 0, icon: GitBranch, color: 'text-green-600' },
          { label: 'Decisions Linked', value: portfolio?.summary?.decisions_linked || 0, icon: Link2, color: 'text-blue-600' },
          { label: 'Doc Versions', value: portfolio?.summary?.doc_versions || 0, icon: FileText, color: 'text-purple-600' },
          { label: 'Drift (30d)', value: portfolio?.summary?.drift_incidents_30d || 0, icon: AlertTriangle, color: 'text-amber-600' },
          { label: 'Drift (90d)', value: portfolio?.summary?.drift_incidents_90d || 0, icon: TrendingDown, color: 'text-red-600' }
        ].map((stat, idx) => (
          <div key={idx} className="bg-white rounded-xl border border-gray-200 p-4">
            <stat.icon className={`w-5 h-5 ${stat.color} mb-2`} />
            <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
            <div className="text-xs text-gray-500">{stat.label}</div>
          </div>
        ))}
      </div>
      
      {/* Tenant List */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Client Tenants</h2>
        
        {portfolio?.tenants?.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-gray-600 mb-2">No client tenants</h3>
            <p className="text-sm text-gray-500">
              You don't have access to any client tenants yet.
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {portfolio?.tenants?.map(tenant => (
              <div
                key={tenant.tenant_id}
                className="bg-white rounded-xl border border-gray-200 p-5 hover:border-purple-300 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{tenant.name}</h3>
                      <p className="text-sm text-gray-500">{tenant.project_count} projects</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setExportModal({ open: true, tenant })}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 text-sm font-medium"
                    data-testid={`export-btn-${tenant.tenant_id}`}
                  >
                    <Download className="w-4 h-4" />
                    Export Bundle
                  </button>
                </div>
                
                {/* Governance Indicators */}
                <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
                  <div className="flex items-center gap-2 text-sm">
                    <GitBranch className="w-4 h-4 text-green-500" />
                    <span className="text-gray-600">{tenant.approved_baselines} baselines</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Link2 className="w-4 h-4 text-blue-500" />
                    <span className="text-gray-600">{tenant.decisions_linked} decisions</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4 text-purple-500" />
                    <span className="text-gray-600">{tenant.doc_versions} docs</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <AlertTriangle className={`w-4 h-4 ${tenant.drift_incidents_30d > 0 ? 'text-amber-500' : 'text-gray-300'}`} />
                    <span className="text-gray-600">{tenant.drift_incidents_30d} drift (30d)</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <TrendingDown className={`w-4 h-4 ${tenant.drift_incidents_90d > 5 ? 'text-red-500' : 'text-gray-300'}`} />
                    <span className="text-gray-600">{tenant.drift_incidents_90d} drift (90d)</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Export Modal */}
      <ExportBundleModal
        isOpen={exportModal.open}
        onClose={() => setExportModal({ open: false, tenant: null })}
        tenant={exportModal.tenant}
        projects={projects[exportModal.tenant?.tenant_id] || []}
        onExport={handleExport}
      />
    </div>
  );
};

export default PartnerPortfolioPage;
